import { useTheme, THEMES, THEME_LABELS, THEME_DESCRIPTIONS, THEME_SWATCHES } from "@/contexts/ThemeContext";

interface ThemePickerProps {
  compact?: boolean;
}

export function ThemePicker({ compact = false }: ThemePickerProps) {
  const { theme, setTheme } = useTheme();

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {THEMES.map((t) => {
          const swatch = THEME_SWATCHES[t];
          const isActive = theme === t;
          return (
            <button
              key={t}
              onClick={() => setTheme(t)}
              title={THEME_LABELS[t]}
              aria-label={`Switch to ${THEME_LABELS[t]} theme`}
              aria-pressed={isActive}
              className={
                "relative h-6 w-6 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 " +
                (isActive ? "ring-2 ring-offset-2 ring-stone-400 scale-110" : "hover:scale-110")
              }
              style={{ background: swatch.bg }}
            >
              <span
                className="absolute inset-[3px] rounded-full"
                style={{ background: swatch.card }}
              />
              {isActive && (
                <span
                  className="absolute inset-0 flex items-center justify-center text-[8px] font-bold z-10"
                  style={{ color: swatch.text }}
                >
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {THEMES.map((t) => {
        const swatch = THEME_SWATCHES[t];
        const isActive = theme === t;
        return (
          <button
            key={t}
            onClick={() => setTheme(t)}
            aria-label={`Switch to ${THEME_LABELS[t]} theme`}
            aria-pressed={isActive}
            className={
              "group relative flex flex-col items-start gap-2 rounded-2xl border-2 p-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1 " +
              (isActive
                ? "border-stone-900 shadow-sm"
                : "border-stone-200 hover:border-stone-400")
            }
          >
            <div className="flex w-full items-center gap-1.5">
              <span
                className="h-5 w-5 rounded-full ring-1 ring-black/10"
                style={{ background: swatch.bg }}
              />
              <span
                className="h-5 w-5 rounded-full ring-1 ring-black/10"
                style={{ background: swatch.card }}
              />
              <span
                className="ml-auto flex h-4 w-4 items-center justify-center rounded-full text-[10px]"
                style={{
                  background: isActive ? swatch.text : "transparent",
                  color: isActive ? swatch.bg : "transparent",
                }}
              >
                {isActive ? "✓" : ""}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold text-stone-900 leading-none mb-0.5">
                {THEME_LABELS[t]}
              </p>
              <p className="text-[10px] text-stone-500 leading-snug">
                {THEME_DESCRIPTIONS[t]}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
